import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Rectangle, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASEMAP_ATTRIBUTION, BASEMAP_URL } from '../utils/basemap';
import type { AnalyticsData } from '../api/services/analyticsService';

/**
 * Carte des zones (section Analytics) : la Tunisie découpée en cases
 * d'environ 1 km, colorées selon le nombre de demandes, de locations
 * terminées ou d'annonces en ligne. Les cases viennent de l'API, déjà
 * agrégées : aucune position exacte n'arrive jusqu'ici.
 *
 * Couleur séquentielle (une teinte, du clair au foncé) en cinq classes
 * calculées sur les valeurs réelles (quantiles), pour qu'une poignée de
 * cases très actives n'écrase pas tout le reste en une seule couleur pâle.
 */

type Metric = 'requests' | 'completed' | 'listings';
type Cell = AnalyticsData['zones']['cells'][number];

const METRICS: { id: Metric; label: string; unit: [string, string]; ramp: string[] }[] = [
  { id: 'requests', label: 'Demandes', unit: ['demande', 'demandes'], ramp: ['#C7D2FE', '#A5B4FC', '#818CF8', '#4F46E5', '#312E81'] },
  { id: 'completed', label: 'Terminées', unit: ['location terminée', 'locations terminées'], ramp: ['#99F6E4', '#5EEAD4', '#2DD4BF', '#0D9488', '#134E4A'] },
  { id: 'listings', label: 'Annonces en ligne', unit: ['annonce en ligne', 'annonces en ligne'], ramp: ['#E5E7EB', '#D1D5DB', '#9CA3AF', '#4B5563', '#1F2937'] }
];

// Raccourcis de cadrage : là où Dingo tourne.
const AREAS: { id: string; label: string; bounds: [[number, number], [number, number]] | null }[] = [
  { id: 'all', label: 'Tout', bounds: null },
  { id: 'tunis', label: 'Grand Tunis', bounds: [[36.70, 10.00], [36.97, 10.40]] },
  { id: 'sousse', label: 'Sousse · Monastir', bounds: [[35.60, 10.50], [35.90, 10.90]] },
  { id: 'sfax', label: 'Sfax', bounds: [[34.65, 10.60], [34.85, 10.85]] }
];

const TUNISIA: [[number, number], [number, number]] = [[33.0, 7.5], [37.4, 11.6]];

/**
 * Seuils de classe : quantiles des valeurs non nulles, strictement croissants
 * et au-dessus du minimum — la classe 0 va du minimum au premier seuil exclu.
 * Peu de valeurs distinctes (1, 2, 3…) donnent autant de classes, pas plus.
 */
function classBreaks(values: number[], classes: number): { min: number; breaks: number[] } {
  const sorted = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (sorted.length === 0) return { min: 0, breaks: [] };
  const min = sorted[0];
  const breaks: number[] = [];
  for (let i = 1; i < classes; i++) {
    const v = sorted[Math.min(sorted.length - 1, Math.floor((i / classes) * sorted.length))];
    if (v > min && (breaks.length === 0 || v > breaks[breaks.length - 1])) breaks.push(v);
  }
  return { min, breaks };
}

const classOf = (value: number, breaks: number[]) => {
  let index = 0;
  while (index < breaks.length && value >= breaks[index]) index += 1;
  return index;
};

const Frame = ({ bounds }: { bounds: L.LatLngBoundsExpression }) => {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 13, animate: true });
  }, [map, bounds]);
  return null;
};

const ZoneMap = ({ zones, periodLabel }: { zones: AnalyticsData['zones']; periodLabel: string }) => {
  const [metric, setMetric] = useState<Metric>('requests');
  const [area, setArea] = useState('all');
  const spec = METRICS.find((m) => m.id === metric) || METRICS[0];

  const cells = useMemo(() => zones.cells.filter((c) => c[metric] > 0), [zones, metric]);
  const { min, breaks } = useMemo(() => classBreaks(cells.map((c) => c[metric]), spec.ramp.length), [cells, metric, spec.ramp.length]);
  const ramp = spec.ramp.slice(spec.ramp.length - (breaks.length + 1));
  const total = cells.reduce((sum, c) => sum + c[metric], 0);

  const bounds = useMemo<L.LatLngBoundsExpression>(() => {
    const chosen = AREAS.find((a) => a.id === area);
    if (chosen?.bounds) return chosen.bounds;
    if (cells.length === 0) return TUNISIA;
    const d = zones.cell_deg;
    const south = Math.min(...cells.map((c) => c.south));
    const west = Math.min(...cells.map((c) => c.west));
    const north = Math.max(...cells.map((c) => c.south + d));
    const east = Math.max(...cells.map((c) => c.west + d));
    return [[south, west], [north, east]];
  }, [area, cells, zones.cell_deg]);

  const legend = ramp.map((color, i) => {
    const low = i === 0 ? min : breaks[i - 1];
    const high = i < breaks.length ? breaks[i] - 1 : null;
    return { color, label: high === null ? `${low}+` : low === high ? `${low}` : `${low}–${high}` };
  });

  const describe = (c: Cell) => [
    `${c.requests} ${c.requests > 1 ? 'demandes' : 'demande'}`,
    `${c.completed} ${c.completed > 1 ? 'terminées' : 'terminée'}`,
    `${c.listings} ${c.listings > 1 ? 'annonces en ligne' : 'annonce en ligne'}`
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Couche affichée" className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5 text-[13px] font-medium">
          {METRICS.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={metric === m.id}
              onClick={() => setMetric(m.id)}
              className={`rounded-lg px-2.5 py-1.5 transition-colors sm:px-3.5 ${metric === m.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {AREAS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setArea(a.id)}
              aria-pressed={area === a.id}
              className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${area === a.id ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative mt-3 h-[340px] overflow-hidden rounded-xl border border-gray-100 sm:h-[440px]">
        {/* Fond désaturé sur cette carte seulement : la couleur est réservée aux données. */}
        <style>{'.dg-zone-map .leaflet-tile-pane{filter:grayscale(1) brightness(1.06) contrast(0.9)}'}</style>
        <MapContainer bounds={TUNISIA} scrollWheelZoom={false} className="dg-zone-map h-full w-full" style={{ background: '#F3F4F6' }}>
          <TileLayer url={BASEMAP_URL} attribution={BASEMAP_ATTRIBUTION} />
          <Frame bounds={bounds} />
          {cells.map((c) => {
            const color = ramp[classOf(c[metric], breaks)] || ramp[ramp.length - 1];
            return (
              <Rectangle
                key={`${metric}:${c.south}:${c.west}`}
                bounds={[[c.south, c.west], [c.south + zones.cell_deg, c.west + zones.cell_deg]]}
                pathOptions={{ color: '#FFFFFF', weight: 1, fillColor: color, fillOpacity: 0.72 }}
              >
                <Tooltip direction="top" sticky>
                  <span className="text-[12px]">
                    <strong>{c[metric]} {c[metric] > 1 ? spec.unit[1] : spec.unit[0]}</strong>
                    <br />
                    {describe(c).filter((_, i) => METRICS[i].id !== metric).join(' · ')}
                  </span>
                </Tooltip>
              </Rectangle>
            );
          })}
        </MapContainer>
        {cells.length === 0 && (
          <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
            <p className="rounded-xl bg-white/95 px-4 py-2 text-sm text-gray-600 shadow">
              {metric === 'listings' ? 'Aucune annonce en ligne localisée.' : `Aucune position connue sur les ${periodLabel}.`}
            </p>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[12px] text-gray-500">
        {legend.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label={`Légende : ${spec.unit[1]} par case`}>
            <span>{spec.unit[1].charAt(0).toUpperCase() + spec.unit[1].slice(1)} par case</span>
            {legend.map((l) => (
              <span key={l.color} className="flex items-center gap-1">
                <span className="h-3 w-4 rounded-sm" style={{ background: l.color, opacity: 0.85 }} />
                <span className="tabular-nums">{l.label}</span>
              </span>
            ))}
          </div>
        ) : <span />}
        <span>
          {total > 0 && <>{total} {total > 1 ? spec.unit[1] : spec.unit[0]} sur {cells.length} {cells.length > 1 ? 'cases' : 'case'} · </>}
          case ≈ 1 km, positions regroupées
        </span>
      </div>
    </div>
  );
};

export default ZoneMap;
